import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { listRewardPoints } from "../../Redux/Actions/rewardsAction";
import { FiInfo } from "react-icons/fi";
import "./customPopup.css";

const CustomRewardPointsPopup = ({ onClose, onSubmit }) => {
  const dispatch = useDispatch();
  const rawPointsList = useSelector((state) => state.rewards.pointsList);
  const pointsList = React.useMemo(
    () => rawPointsList ?? { data: [], count: 0 },
    [rawPointsList]
  );

  const [points, setPoints] = useState("");
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 10;
  const [loading, setLoading] = useState(false);
  const [allData, setAllData] = useState([]);
  const [hasMore, setHasMore] = useState(true);

  const scrollContainerRef = useRef(null);

  // Fetch data when popup opens or page changes
  useEffect(() => {
    if (showInfoPopup) {
      setLoading(true);
      dispatch(listRewardPoints({ page, limit, is_deleted: false })).then(() => {
        setLoading(false);
      });
    }
  }, [dispatch, showInfoPopup, page]);

  // Append new data when pointsList updates
  useEffect(() => {
    if (showInfoPopup && Array.isArray(pointsList.data)) {
      if (page === 1) {
        setAllData(pointsList.data);
      } else {
        setAllData((prev) => [...prev, ...pointsList.data]);
      }
      const totalPages = Math.ceil(pointsList.count / limit);
      setHasMore(page < totalPages);
    }
  }, [pointsList, showInfoPopup, page]);

  // Reset state and open popup
  const openInfoPopup = () => {
    setPage(1);
    setAllData([]);
    setHasMore(true);
    setShowInfoPopup(true);
  };

  const closeInfoPopup = () => {
    setShowInfoPopup(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (points && !isNaN(points)) {
      onSubmit(points);
      onClose();
    } else {
      alert("Please enter valid reward points");
    }
  };

  // Scroll handler to load more on reaching bottom
  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el || loading || !hasMore) return;

    if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
      setPage((prev) => prev + 1);
    }
  };

  return (
    <>
      {/* ══════════════════════════════════════════════
          Main Popup – Assign Reward Points
      ══════════════════════════════════════════════ */}
      <div className="crp-overlay" onClick={onClose}>
        <div className="crp-modal" onClick={(e) => e.stopPropagation()}>

          {/* Header */}
          <div className="crp-header">
            <div className="crp-header__left">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              <span className="crp-title">Assign Reward Points</span>
            </div>
            <button className="crp-close-x" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>

          {/* Body */}
          <form className="crp-body" onSubmit={handleSubmit}>
            <div className="crp-field">
              <label htmlFor="points" className="crp-label">
                Reward Points
              </label>
              <div className="crp-input-group">
                <input
                  id="points"
                  type="number"
                  className="crp-input"
                  value={points}
                  onChange={(e) => setPoints(e.target.value)}
                  placeholder="Enter points"
                  min="0"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={openInfoPopup}
                  style={{
                    borderRadius: "50%",
                    // width: "22px",
                    // height: "24px",
                    fontWeight: "bold",
                    lineHeight: "22px",
                    cursor: "pointer",
                    // border: "1px solid white",
                    color: "#0D152B",
                    backgroundColor: "transparent",
                    marginBottom: "13px",
                    marginTop: "14px"
                  }}
                  title="View reward points list"
                >
                  <FiInfo size={21} />
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="crp-footer">
              <button type="button" className="crp-btn crp-btn--secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="crp-btn crp-btn--primary">
                Submit
              </button>
            </div>
          </form>

        </div>
      </div>

      {/* ══════════════════════════════════════════════
          Info Popup – Reward Points List
      ══════════════════════════════════════════════ */}
      {showInfoPopup && (
        <div className="crp-info-overlay" onClick={closeInfoPopup}>
          <div className="crp-info-modal" onClick={(e) => e.stopPropagation()}>

            {/* Header */}
            <div className="crp-info-header">
              <div className="crp-info-header__left">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 11l3 3L22 4" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
                <span className="crp-info-title">Reward Points Reference</span>
              </div>
              <button className="crp-info-close" onClick={closeInfoPopup} aria-label="Close">
                ✕
              </button>
            </div>

            {/* Table Container */}
            <div
              className="crp-info-body"
              ref={scrollContainerRef}
              onScroll={handleScroll}
            >
              {allData.length > 0 ? (
                <table className="crp-table">
                  <thead>
                    <tr>
                      <th>Min Value</th>
                      <th>Max Value</th>
                      <th>Reward Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allData.map((item) => (
                      <tr key={item._id}>
                        <td>{item.min_value.toLocaleString()}</td>
                        <td>{item.max_value.toLocaleString()}</td>
                        <td>
                          <span className="crp-points-badge">
                            {item.reward_points}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="crp-empty">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <p>No reward points data found.</p>
                </div>
              )}

              {loading && (
                <div className="crp-loading">
                  <div className="crp-spinner" />
                  <span>Loading more...</span>
                </div>
              )}
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default CustomRewardPointsPopup;